import React, { FC } from 'react';
import styled from 'styled-components';
import { AdvancedSelect, compactSelectStyles } from 'src/components/widgets/select';
import { PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import { ZTooltip } from 'src/components/widgets/ztooltip';

// IL COMANDO CHE APPARTIENE A QUESTA RIGA, in coda al campo. Serve quando il valore scelto è
// un'altra cosa che vive altrove — il materiale di un oggetto, l'oggetto bersaglio di una luce —
// e da qui la si vuole ritrovare. Sta accanto al campo perché è di quel campo: in barra
// sarebbe un comando in cerca di un soggetto.
const PropertyAction = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    opacity: 0.72;
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
    }

    &:hover:not(:disabled),
    &:focus-visible {
        opacity: 1;
        background: ${(props) => props.theme.app.pageBackgroundColor};
        outline: none;
    }

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }
`;

export type PropertiesPanelSelectValue = { value: string | number; label: string } | null | undefined;

export type PropertiesPanelAdvancedSelectPropertyProps = {
    menuPlacement?: 'auto' | 'bottom' | 'top';
};

// UN MENU DEVE POTER USCIRE DALLA SUA CARD. La card della scena ritaglia ciò che sporge — è
// l'`overflow: hidden` che le tiene gli angoli tondi e le chiude il corpo quando è piegata — e un
// elenco che si apre lì dentro veniva tagliato via prima di comparire: si apriva davvero, non lo
// si vedeva. Portandolo in fondo alla pagina esce dal ritaglio, e sopra i pannelli fluttuanti
// dell'editor. `auto` perché una card in fondo alla colonna deve aprirlo in su, o finisce sotto il
// bordo della finestra.
//
// Vale per ogni menu del pannello, non solo per quello del tipo di una luce: la card è la stessa.
const escapesTheCard = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuPlacement: 'auto' as const,
    // UNA TENDINA DI POCHE VOCI SI SCEGLIE, NON SI CERCA (prototipo; utente 2026-10-21): niente cursore di testo nel
    // campo. Chi ha un elenco lungo lo riaccende con `selectCustomProps`.
    isSearchable: false
};

export type PropertiesPanelSelectPropertyProps = {
    id: string;
    label: React.ReactNode;
    options: NonNullable<PropertiesPanelSelectValue>[];
    value: PropertiesPanelSelectValue;
    onChange: (value: PropertiesPanelSelectValue) => void;
    selectCustomProps?: PropertiesPanelAdvancedSelectPropertyProps;
    disabled?: boolean;
    /** Un comando che riguarda il valore scelto, non la riga: «ritrovamelo nella sua lista». */
    action?: { icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean };
    /** La riga che spiega il valore scelto, sotto il campo e larga quanto la card. */
    caption?: React.ReactNode;
};

const SelectBox = styled.div`
    flex: 1;
    min-width: 0;
`;

// SOTTO IL CAMPO, non accanto al nome: dice che cosa vuol dire il valore scelto, e cambia con lui.
const SelectCaption = styled.p<{ $disabled?: boolean }>`
    margin: -4px 0 0;
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    line-height: 1.4;
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
`;
// IL MENU DA SOLO, senza la riga intorno — come `PropertiesPanelSwitch` sta accanto a
// `PropertiesPanelSwitchProperty`. Serve dove il soggetto non è un parametro fra i parametri ma
// la card intera: il tipo di una luce sta nella TESTATA, e lì il nome ce l'ha già la testata —
// una seconda etichetta accanto sarebbe la stessa parola due volte.
const BareSelectBox = styled.div`
    min-width: 140px;
`;

export const PropertiesPanelBareSelect: FC<Omit<PropertiesPanelSelectPropertyProps, 'label' | 'action'> & { ariaLabel?: string }> = ({
    id,
    options,
    value,
    onChange,
    disabled,
    selectCustomProps,
    ariaLabel
}) => (
    <BareSelectBox aria-label={ariaLabel}>
        <AdvancedSelect
            id={id}
            options={options}
            value={value}
            onChange={(newValue) => onChange?.(newValue)}
            isDisabled={disabled}
            styles={compactSelectStyles as any}
            {...escapesTheCard}
            {...selectCustomProps}
        />
    </BareSelectBox>
);

export const PropertiesPanelSelectProperty: FC<PropertiesPanelSelectPropertyProps> = ({
    id,
    label,
    options,
    value,
    onChange,
    disabled,
    selectCustomProps,
    action,
    caption
}) => {
    return (
        <>
            <PropertiesPanelProperty disabled={disabled}>
                <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
                <PropertiesPanelControl>
                    {/* LA TENDINA È QUELLA DELLO STORYBOOK DI ZAKEKE (utente, 2026-10-20), nella misura compatta e larga quanto
                        la colonna del valore: lasciata a sé si stringeva sul valore scelto. */}
                    <SelectBox>
                        <AdvancedSelect
                            id={id}
                            options={options}
                            value={value}
                            onChange={(newValue) => {
                                onChange?.(newValue);
                            }}
                            isDisabled={disabled}
                            styles={compactSelectStyles as any}
                            {...escapesTheCard}
                            {...selectCustomProps}
                        />
                    </SelectBox>
                    {action && (
                        <ZTooltip title={action.label} position='top'>
                            <PropertyAction type='button' onClick={action.onClick} disabled={action.disabled} aria-label={action.label}>
                                {action.icon}
                            </PropertyAction>
                        </ZTooltip>
                    )}
                </PropertiesPanelControl>
            </PropertiesPanelProperty>
            {caption && <SelectCaption $disabled={disabled}>{caption}</SelectCaption>}
        </>
    );
};
